import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Shield, RotateCw } from "lucide-react";

export const Route = createFileRoute("/admin/mcp-audit")({
  component: Page,
  head: () => ({
    meta: [
      { title: "MCP audit log — Admin" },
      { name: "description", content: "Prohlížení a filtrování audit logů MCP nástrojů." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Status = "ok" | "error" | "rate_limited" | "unauthorized";
type Row = {
  id: string;
  created_at: string;
  user_id: string | null;
  client_id: string | null;
  tool_name: string;
  status: Status;
  error_message: string | null;
  duration_ms: number | null;
  input: unknown;
};

const TOOLS = [
  "search_businesses",
  "get_business",
  "catalog_stats",
  "list_my_businesses",
  "list_my_orders",
  "search_audit_log",
];

const STATUS_VARIANT: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  ok: "secondary",
  error: "destructive",
  rate_limited: "outline",
  unauthorized: "destructive",
};

const fmt = (s: string) => new Date(s).toLocaleString("cs-CZ");

function Page() {
  const [userId, setUserId] = useState("");
  const [toolName, setToolName] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [limit, setLimit] = useState(100);

  const filters = useMemo(
    () => ({ userId: userId.trim(), toolName, status, since, until, limit }),
    [userId, toolName, status, since, until, limit],
  );

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ["admin", "mcp-audit", filters],
    queryFn: async (): Promise<Row[]> => {
      let q = supabase
        .from("mcp_audit_log")
        .select("id, created_at, user_id, client_id, tool_name, status, error_message, duration_ms, input")
        .order("created_at", { ascending: false })
        .limit(filters.limit);
      if (filters.userId) q = q.eq("user_id", filters.userId);
      if (filters.toolName !== "all") q = q.eq("tool_name", filters.toolName);
      if (filters.status !== "all") q = q.eq("status", filters.status);
      if (filters.since) q = q.gte("created_at", new Date(filters.since).toISOString());
      if (filters.until) q = q.lt("created_at", new Date(filters.until).toISOString());
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as Row[];
    },
  });

  const reset = () => {
    setUserId("");
    setToolName("all");
    setStatus("all");
    setSince("");
    setUntil("");
    setLimit(100);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5" />
        <h1 className="text-2xl font-semibold">MCP audit log</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtry</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Label htmlFor="uid">User ID (UUID)</Label>
            <Input
              id="uid"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="např. 3f2a…"
            />
          </div>
          <div>
            <Label>Tool</Label>
            <Select value={toolName} onValueChange={setToolName}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny</SelectItem>
                {TOOLS.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všechny</SelectItem>
                <SelectItem value="ok">ok</SelectItem>
                <SelectItem value="error">error</SelectItem>
                <SelectItem value="rate_limited">rate_limited</SelectItem>
                <SelectItem value="unauthorized">unauthorized</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="since">Od</Label>
            <Input id="since" type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="until">Do</Label>
            <Input id="until" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="limit">Limit</Label>
            <Input
              id="limit"
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(500, Number(e.target.value) || 100)))}
            />
          </div>
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-6">
            <Button onClick={() => refetch()} disabled={isFetching} className="gap-1.5">
              <RotateCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Načíst
            </Button>
            <Button variant="ghost" onClick={reset}>Vyčistit</Button>
            <div className="ml-auto text-xs text-muted-foreground">
              {data ? `${data.length} záznam(ů)` : ""}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-4 text-sm text-destructive">{(error as Error).message}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Čas</TableHead>
                  <TableHead>Tool</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">ms</TableHead>
                  <TableHead>Chyba / Input</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs">{fmt(r.created_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{r.tool_name}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {r.user_id ? r.user_id.slice(0, 8) + "…" : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {r.client_id ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.duration_ms ?? "—"}</TableCell>
                    <TableCell className="max-w-[420px]">
                      {r.error_message ? (
                        <div className="text-xs text-destructive">{r.error_message}</div>
                      ) : (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground">input</summary>
                          <pre className="mt-1 overflow-auto rounded bg-muted p-2 text-[11px]">
{JSON.stringify(r.input, null, 2)}
                          </pre>
                        </details>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!isFetching && (data?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                      Žádné záznamy neodpovídají filtrům.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
